import { UzButton } from '@utazzo/ui';
import { useEffect, useRef } from 'react';

/**
 * The inactivity warning. Deliberately NOT built on UzDialog / Radix: the
 * shared DialogContent always renders a close "X", and we need a modal with
 * ZERO dismiss paths - no X, no Escape, no backdrop-click. The ONLY way out is
 * the single "Extend session" button. Rendered at a very high z-index so it
 * sits above any form dialog that happened to be open when idle struck.
 */

interface IdleWarningDialogProps {
  remainingMs: number;
  onExtend: () => void;
  extending: boolean;
}

const formatMmSs = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const IdleWarningDialog = ({
  remainingMs,
  onExtend,
  extending,
}: IdleWarningDialogProps): React.JSX.Element => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-warning-title"
      aria-describedby="idle-warning-desc"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4"
      // The backdrop must never dismiss the dialog.
      onMouseDown={(e) => e.preventDefault()}
      // Keep key events (Esc, app hotkeys) from leaking to the app behind us.
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="bg-background w-full max-w-md rounded-xl border p-6 shadow-2xl">
        <h2 id="idle-warning-title" className="text-foreground text-lg font-semibold">
          Session about to expire
        </h2>
        <p id="idle-warning-desc" className="text-muted-foreground mt-2 text-sm">
          You have been inactive. For your security you will be logged out in{' '}
          <span className="text-foreground font-semibold tabular-nums">
            {formatMmSs(remainingMs)}
          </span>
          .
        </p>
        <div className="mt-6 flex justify-end">
          <UzButton ref={buttonRef} onClick={onExtend} loading={extending}>
            Extend session
          </UzButton>
        </div>
      </div>
    </div>
  );
};
